import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  ToggleButton, 
  ToggleButtonGroup, 
  useTheme,
  alpha,
  IconButton,
  useMediaQuery,
  Chip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import { FormProvider, UseFormReturn } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import dayjs from 'dayjs';
import { FormTextField, FormMentionTextField, SaveIndicator } from '@personal-dashboard/ui';
import { NoteData, NoteFormData } from '../queries';
import { SaveStatus } from '../../../hooks/useAutosave';
import { getLinkedPeopleIds } from '../../../utils/mention';

interface NoteEditorProps {
  selectedNote: NoteData;
  methods: UseFormReturn<NoteFormData>;
  saveStatus: SaveStatus;
  onBack?: () => void;
  people: Array<{ id: string; name: string }>;
}

const NoteEditor: React.FC<NoteEditorProps> = ({ selectedNote, methods, saveStatus, onBack, people }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [viewMode, setViewMode] = useState<'edit' | 'preview'>('edit');
  const formValues = methods.watch();

  const linkedPeople = React.useMemo(() => {
    const ids = getLinkedPeopleIds(formValues.content || '', people);
    return people.filter(p => ids.includes(p.id));
  }, [formValues.content, people]);

  return (
    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', bgcolor: 'background.paper' }}>
      <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {isMobile && onBack && (
            <IconButton onClick={onBack} size="small" sx={{ mr: 1 }}>
              <ArrowBackIcon />
            </IconButton>
          )}
          <ToggleButtonGroup
            size="small"
            value={viewMode}
            exclusive
            onChange={(_, val) => val && setViewMode(val)}
            aria-label="view mode"
          >
            <ToggleButton value="edit" aria-label="edit">
              <EditIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="preview" aria-label="preview">
              <VisibilityIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
          <SaveIndicator status={saveStatus} />
        </Box>
        <Typography variant="caption" color="text.disabled">
          {selectedNote?.updatedAt ? `Last saved: ${dayjs(isNaN(Number(selectedNote.updatedAt)) ? selectedNote.updatedAt : Number(selectedNote.updatedAt)).format('h:mm:ss A')}` : ''}
        </Typography>
      </Box>

      <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 4 }}>
        <FormProvider {...methods}>
          {viewMode === 'edit' ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 800, mx: 'auto' }}>
              <FormTextField 
                name="title" 
                placeholder="Note Title" 
                variant="standard" 
                fullWidth
                InputProps={{ 
                  disableUnderline: true,
                  sx: { fontSize: '2rem', fontWeight: 'bold', mb: 2 } 
                }}
              />
              <FormMentionTextField 
                name="content" 
                placeholder="Start writing (Markdown supported)..." 
                multiline 
                fullWidth 
                variant="standard"
                people={people}
                InputProps={{ 
                  disableUnderline: true, 
                  sx: { 
                    fontSize: '1.1rem', 
                    lineHeight: 1.8, 
                    fontFamily: 'JetBrains Mono, monospace',
                    minHeight: '60vh',
                    alignItems: 'flex-start'
                  } 
                }}
              />
            </Box>
          ) : (
            <Box sx={{ 
              maxWidth: 800, 
              mx: 'auto', 
              '& .markdown-body': { 
                color: 'text.primary',
                lineHeight: 1.7,
                fontSize: '1.1rem',
                '& h1, & h2, & h3': { mt: 4, mb: 2, fontWeight: 'bold' },
                '& p': { mb: 2 },
                '& ul, & ol': { pl: 4, mb: 2 },
                '& code': { bgcolor: alpha(theme.palette.primary.main, 0.1), p: 0.5, borderRadius: 1, fontFamily: 'JetBrains Mono, monospace', fontSize: '0.9em' },
                '& pre': { bgcolor: alpha(theme.palette.primary.main, 0.05), p: 2, borderRadius: 2, overflowX: 'auto', mb: 2 },
                '& blockquote': { borderLeft: `4px solid ${theme.palette.primary.main}`, pl: 2, py: 1, my: 2, bgcolor: alpha(theme.palette.primary.main, 0.02), fontStyle: 'italic' },
                '& table': { width: '100%', borderCollapse: 'collapse', mb: 2 },
                '& th, & td': { border: `1px solid ${theme.palette.divider}`, p: 1 }
              } 
            }}>
              <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 2 }}>
                {formValues.title || 'Untitled'}
              </Typography>
              {linkedPeople.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                  {linkedPeople.map((person) => (
                    <Chip
                      key={person.id}
                      icon={<PersonIcon />}
                      label={person.name}
                      onClick={() => navigate(`/people?search=${encodeURIComponent(person.name)}`)}
                      variant="outlined"
                      clickable
                    />
                  ))}
                </Box>
              )}
              <Box className="markdown-body">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {formValues.content}
                </ReactMarkdown>
              </Box>
            </Box>
          )}
        </FormProvider>
      </Box>
    </Box>
  );
};

export default NoteEditor;
